/* eslint-disable indent */
import { UploadOutlined } from "@ant-design/icons";
import {
  Modal,
  Form,
  Input,
  DatePicker,
  Upload,
  Button,
  Typography,
  Image,
  Select,
  InputNumber,
  theme
} from "antd";
import axios from "axios";
import dayjs from "dayjs";
import { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";

import { backendUrlByEnv } from "../../../constants/routes";
import { showErrorNotification } from "../../../utils/notification";
import { COURSE_SESSION_STATUS } from "../index";

const { Item } = Form;
const { TextArea } = Input;
const { Text } = Typography;
const { useToken } = theme;

// Auto-assigned phase priorities:
//   UPCOMING = 98
//   LIVE     = 198
//   PENDING  = 48
const PHASE_DEFAULT_PRIORITIES = { upcoming: 98, live: 198, pending: 48 };
const BANNER_PRIORITY_MIN = 1;
const BANNER_PRIORITY_MAX = 1000;

/**
 * Preset instructor names for the session form.
 * Admins can also type a custom name.
 */
const INSTRUCTOR_NAME_PRESETS = [
  { label: "Priyansh Agarwal", value: "Priyansh Agarwal" },
  { label: "Viraj Chandra", value: "Viraj Chandra" }
];

// ── Default banner templates ────────────────────────────────────────────────────

/**
 * Generates a default UPCOMING butterbar message based on session title and start date.
 * @param {string} title
 * @param {string|dayjs.Dayjs} [startDate]
 * @returns {string}
 */
const getDefaultUpcomingBanner = (title, startDate) => {
  const formattedDate = startDate ? dayjs(startDate).format("DD MMM YYYY, hh:mm A [IST]") : null;
  const timeText = formattedDate ? `is scheduled for ${formattedDate}` : "is scheduled";
  return `🔔 $\\textbf{Upcoming:}$ The session $\\textbf{"${title || "Untitled"}"}$ ${timeText}.`;
};

/**
 * Generates a default LIVE butterbar message based on session title.
 * @param {string} title
 * @returns {string}
 */
const getDefaultLiveBanner = (title) =>
  `🔴 $\\textbf{Live Now:}$ The session $\\textbf{"${
    title || "Untitled"
  }"}$ is currently live! Join now.`;

/**
 * Generates a default PENDING butterbar message based on session title.
 * @param {string} title
 * @returns {string}
 */
const getDefaultPendingBanner = (title) =>
  `⏳ $\\textbf{Recording Pending:}$ The session $\\textbf{"${
    title || "Untitled"
  }"}$ has ended. The recording will be uploaded soon.`;

/**
 * Mapping of phase indices to their default banner message templates.
 */
const DEFAULT_BANNER_TEMPLATES = {
  0: getDefaultUpcomingBanner,
  1: getDefaultLiveBanner,
  2: getDefaultPendingBanner
};

// ── Component ──────────────────────────────────────────────────────────────────

/**
 * CreateEditSessionModal
 *
 * Modal form for creating and editing course sessions.
 *
 * Banner messages are collected as three separate text areas (upcoming / live /
 * pending) and assembled into the `bannerMessages` array format expected by the
 * backend (`CourseSessionService.createSession` / `updateSession`).
 *
 * The `linkedCourseIds` field (multi-select) lets admins restrict which
 * enrolled students see the butterbar announcements.
 *
 * @param {Object}      props
 * @param {boolean}     props.isModalOpen     - Controls modal visibility.
 * @param {Function}    props.setIsModalOpen  - Closes the modal.
 * @param {Function}    props.onSubmit        - Called with the formatted payload.
 * @param {Object|null} props.initialValues   - Existing session to pre-populate, or null.
 */
const CreateEditSessionModal = ({
  isModalOpen = false,
  setIsModalOpen = () => {},
  onSubmit = () => {},
  initialValues = null
}) => {
  const [form] = Form.useForm();
  const { token } = useToken();
  const isEdit = Boolean(initialValues);
  const [uploading, setUploading] = useState(false);
  const [useCustomBanners, setUseCustomBanners] = useState(false);
  const [coursesList, setCoursesList] = useState([]);
  const [fetchingCourses, setFetchingCourses] = useState(false);

  const watchedTitle = Form.useWatch("title", form);
  const watchedDateRange = Form.useWatch("sessionDateRange", form);

  // Auto-update banner templates when title or date range changes (if not using custom banners).
  useEffect(() => {
    if (!useCustomBanners) {
      const startDate = watchedDateRange?.[0];
      form.setFieldsValue({
        upcomingBanner: getDefaultUpcomingBanner(watchedTitle, startDate),
        liveBanner: getDefaultLiveBanner(watchedTitle),
        pendingBanner: getDefaultPendingBanner(watchedTitle)
      });
    }
  }, [watchedTitle, watchedDateRange, useCustomBanners, form]);

  const { env } = useSelector((state) => state?.reducer?.authInfo) || {};
  const ROOT_URL = backendUrlByEnv(env);

  useEffect(() => {
    if (!isModalOpen) {
      return;
    }
    setFetchingCourses(true);
    axios
      .get(`${ROOT_URL}/admin/course-sessions/form-requisites`)
      .then((response) => {
        const list = response.data?.coursesList || [];
        setCoursesList(list);
        if (!initialValues) {
          form.setFieldValue(
            "linkedCourseIds",
            list.map((course) => course.value)
          );
        }
      })
      .catch((error) => showErrorNotification(error))
      .finally(() => setFetchingCourses(false));
  }, [isModalOpen, ROOT_URL, form, initialValues]);

  const handleCancel = () => setIsModalOpen(false);

  // ── Form submission ────────────────────────────────────────────────────────

  const handleSubmit = () => {
    form
      .validateFields()
      .then((formValues) => {
        const {
          sessionDateRange,
          upcomingBanner,
          liveBanner,
          pendingBanner,
          upcomingBannerPriority,
          liveBannerPriority,
          pendingBannerPriority,
          liveUrl,
          tpStreamsAssetId,
          ...restFormValues
        } = formValues;

        const payload = {
          ...restFormValues,
          sessionStartDate: sessionDateRange?.[0]?.toISOString(),
          sessionEndDate: sessionDateRange?.[1]?.toISOString(),
          ...(liveUrl && { liveUrl }),
          ...(tpStreamsAssetId && { tpStreamsAssetId }),
          bannerMessages: [
            {
              bannerType: COURSE_SESSION_STATUS.SESSION_UPCOMING,
              message: upcomingBanner,
              ...(typeof upcomingBannerPriority === "number" && {
                priority: upcomingBannerPriority
              })
            },
            {
              bannerType: COURSE_SESSION_STATUS.SESSION_LIVE,
              message: liveBanner,
              ...(typeof liveBannerPriority === "number" && {
                priority: liveBannerPriority
              })
            },
            {
              bannerType: COURSE_SESSION_STATUS.SESSION_ENDED_RECORDING_PENDING,
              message: pendingBanner,
              ...(typeof pendingBannerPriority === "number" && {
                priority: pendingBannerPriority
              })
            }
          ],
          linkedCourseIds: formValues.linkedCourseIds || []
        };

        onSubmit(payload);
      })
      .catch(() => {});
  };

  // ── Thumbnail upload ──────────────────────────────────────────────────────

  const handleThumbnailUpload = async ({ file }) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await axios.post(
        `${ROOT_URL}/admin/course-sessions/upload-asset`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" }
        }
      );
      form.setFieldValue("thumbnailUrl", response.data);
    } catch (error) {
      showErrorNotification(error);
    } finally {
      setUploading(false);
    }
    return false; // Prevent Ant Design's default upload behaviour.
  };

  // ── Form initial values ────────────────────────────────────────────────────

  // Destructure properties from initialValues upfront to avoid repeated optional property chaining.
  const { instructorName, sessionStartDate, sessionEndDate, title, announcementBanners } =
    initialValues ?? {};

  /**
   * Pre-populates a specific phase banner message. If the admin has already
   * customized it, returns that message; otherwise, resolves the default template.
   *
   * @param {number} phaseIndex - The phase index (0=Upcoming, 1=Live, 2=Pending).
   * @returns {string}
   */
  const getBannerMessage = useCallback(
    (phaseIndex) => {
      const matchingBanner = announcementBanners?.find(
        (banner) => banner.phaseIndex === phaseIndex
      );
      if (matchingBanner?.message) {
        return matchingBanner.message;
      }
      const templateFn = DEFAULT_BANNER_TEMPLATES[phaseIndex];
      if (phaseIndex === 0) {
        return templateFn ? templateFn(title || "", sessionStartDate) : "";
      }
      return templateFn ? templateFn(title || "") : "";
    },
    [announcementBanners, title, sessionStartDate]
  );

  /**
   * Pre-populate initial form values.
   * If initialValues exists (Edit Mode), computes ranges and custom banner messages.
   * Otherwise (Create Mode), initializes default preset templates and default priorities.
   *
   * @type {Object}
   */
  const formInitialValues = initialValues
    ? {
        ...initialValues,
        instructorName: instructorName || "Priyansh Agarwal",
        sessionDateRange:
          sessionStartDate && sessionEndDate
            ? [dayjs(sessionStartDate), dayjs(sessionEndDate)]
            : null,
        upcomingBanner: getBannerMessage(0),
        liveBanner: getBannerMessage(1),
        pendingBanner: getBannerMessage(2),
        upcomingBannerPriority:
          announcementBanners?.find((banner) => banner.phaseIndex === 0)?.priority ??
          PHASE_DEFAULT_PRIORITIES.upcoming,
        liveBannerPriority:
          announcementBanners?.find((banner) => banner.phaseIndex === 1)?.priority ??
          PHASE_DEFAULT_PRIORITIES.live,
        pendingBannerPriority:
          announcementBanners?.find((banner) => banner.phaseIndex === 2)?.priority ??
          PHASE_DEFAULT_PRIORITIES.pending
      }
    : {
        instructorName: "Priyansh Agarwal",
        upcomingBanner: getDefaultUpcomingBanner(""),
        liveBanner: getDefaultLiveBanner(""),
        pendingBanner: getDefaultPendingBanner(""),
        upcomingBannerPriority: PHASE_DEFAULT_PRIORITIES.upcoming,
        liveBannerPriority: PHASE_DEFAULT_PRIORITIES.live,
        pendingBannerPriority: PHASE_DEFAULT_PRIORITIES.pending
      };

  /**
   * Detects whether existing banners differ from templates (e.g. customized banner text).
   * If they differ, automatically flags the form state to use the 'custom banners' view
   * and pre-populates linked course IDs from existing banners.
   */
  useEffect(() => {
    if (!isEdit || !announcementBanners?.length) {
      return;
    }

    const upcomingBannerMessage = getBannerMessage(0);
    const liveBannerMessage = getBannerMessage(1);
    const pendingBannerMessage = getBannerMessage(2);

    // If any active banner differs from the default string templates, switch to custom view.
    if (
      upcomingBannerMessage !== getDefaultUpcomingBanner(title, sessionStartDate) ||
      liveBannerMessage !== getDefaultLiveBanner(title) ||
      pendingBannerMessage !== getDefaultPendingBanner(title)
    ) {
      setUseCustomBanners(true);
    }

    // Pre-populate linked course IDs from existing banners' allowedCourseIds.
    // (All banners for a session share the same allowedCourseIds.)
    const firstBanner = announcementBanners[0];
    if (firstBanner?.allowedCourseIds?.length) {
      form.setFieldValue("linkedCourseIds", firstBanner.allowedCourseIds);
    }
  }, [isEdit, announcementBanners, title, form, getBannerMessage, sessionStartDate]);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Modal
      title={isEdit ? "Edit Course Session" : "Create Course Session"}
      open={isModalOpen}
      onCancel={handleCancel}
      onOk={handleSubmit}
      okText="Save"
      width={640}
      destroyOnClose
    >
      <Form layout="vertical" form={form} initialValues={formInitialValues}>
        <Item name="title" label="Title" rules={[{ required: true, message: "Title is required" }]}>
          <Input placeholder="e.g. DP Deep Dive — AMA + Strategy Session" />
        </Item>

        <Item
          name="description"
          label="Description"
          rules={[{ required: true, message: "Description is required" }]}
        >
          <TextArea rows={3} placeholder="Brief description of the session..." />
        </Item>

        {/* ── Instructor Name ─────────────────────────────────────────── */}
        <Item
          name="instructorName"
          label="Instructor / Session Taker"
          rules={[{ required: true, message: "Instructor name is required" }]}
          tooltip="Select a preset instructor or type a custom name."
        >
          <Select
            showSearch
            allowClear={false}
            placeholder="Select or type instructor name"
            options={INSTRUCTOR_NAME_PRESETS}
            filterOption={(input, option) =>
              option?.label?.toLowerCase().includes(input.toLowerCase())
            }
            dropdownRender={(menu) => (
              <>
                {menu}
                <div style={{ padding: "8px 12px", borderTop: `1px solid ${token.colorBorderSecondary}` }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Type to enter a custom name not listed above
                  </Text>
                </div>
              </>
            )}
            onSearch={(_value) => {
              // Allow free-form text entry by setting the field value directly
              // when user types a name not in the presets.
            }}
            onChange={(value) => {
              form.setFieldValue("instructorName", value);
            }}
            onInputKeyDown={(event) => {
              // Allow user to type a completely custom name
              if (event.key === "Enter") {
                const inputVal = event.target.value;
                if (
                  inputVal &&
                  !INSTRUCTOR_NAME_PRESETS.find((preset) => preset.value === inputVal)
                ) {
                  form.setFieldValue("instructorName", inputVal);
                }
              }
            }}
          />
        </Item>

        <Item
          name="sessionDateRange"
          label="Session Date & Time (Start – End)"
          rules={[{ required: true, message: "Session date range is required" }]}
        >
          <DatePicker.RangePicker
            showTime
            format="YYYY-MM-DD HH:mm"
            style={{ width: "100%" }}
            placeholder={["Start Date & Time", "End Date & Time"]}
          />
        </Item>

        <Item
          name="thumbnailUrl"
          label="Thumbnail"
          rules={[{ required: true, message: "Thumbnail is required" }]}
        >
          <Input placeholder="Paste URL or upload below" />
        </Item>

        <Item>
          <Upload accept="image/*" showUploadList={false} customRequest={handleThumbnailUpload}>
            <Button icon={<UploadOutlined />} loading={uploading}>
              Upload Thumbnail
            </Button>
          </Upload>
        </Item>

        <Item noStyle shouldUpdate={(prev, cur) => prev.thumbnailUrl !== cur.thumbnailUrl}>
          {({ getFieldValue }) => {
            const thumbnailUrl = getFieldValue("thumbnailUrl");
            return thumbnailUrl ? (
              <div style={{ marginBottom: 24, textAlign: "center" }}>
                <Image
                  src={thumbnailUrl}
                  alt="Thumbnail Preview"
                  style={{ maxHeight: 200, borderRadius: 8, objectFit: "cover" }}
                />
              </div>
            ) : null;
          }}
        </Item>

        <Item name="liveUrl" label="Live URL">
          <Input placeholder="https://meet.google.com/... (set when session goes live)" />
        </Item>

        <Item name="tpStreamsAssetId" label="tpStreams Asset ID">
          <Input placeholder="Asset ID (set after recording is processed)" />
        </Item>

        {/* ── Linked Courses ──────────────────────────────────────────── */}
        <Item
          label="Linked Courses (for butterbar gating)"
          name="linkedCourseIds"
          tooltip="Select courses. Only users enrolled in at least one of these courses will see the banners."
          rules={[{ required: true, message: "Please select at least one course" }]}
        >
          <Select
            mode="multiple"
            allowClear
            placeholder="Select courses (required)"
            loading={fetchingCourses}
            options={coursesList}
            optionFilterProp="label"
          />
        </Item>

        {/* ── Notification Banners ───────────────────────────────────────── */}
        <div style={{ marginBottom: 16 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "8px"
            }}
          >
            <Text strong>Notification Banners (Butterbar)</Text>
            <Button size="small" onClick={() => setUseCustomBanners(!useCustomBanners)}>
              {useCustomBanners ? "Use Templates" : "Customize"}
            </Button>
          </div>
          <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 8 }}>
            Auto-generated from session title. Customize individually if needed. Multiple banners
            can be active simultaneously — users see the highest-priority one first and dismiss
            through the stack.
          </Text>

          <Item name="upcomingBanner" label="Upcoming Banner">
            <TextArea rows={2} disabled={!useCustomBanners} />
          </Item>
          <Item
            name="upcomingBannerPriority"
            label={
              <span>
                Upcoming Banner Priority{" "}
                <Text type="secondary" style={{ fontSize: 11 }}>
                  (default: {PHASE_DEFAULT_PRIORITIES.upcoming})
                </Text>
              </span>
            }
            tooltip={`Controls display order when multiple banners compete. Default auto-assigned = ${PHASE_DEFAULT_PRIORITIES.upcoming}. Override only when you get a collision error.`}
            rules={[
              {
                type: "number",
                min: BANNER_PRIORITY_MIN,
                max: BANNER_PRIORITY_MAX,
                message: `Must be ${BANNER_PRIORITY_MIN}–${BANNER_PRIORITY_MAX}`
              }
            ]}
          >
            <InputNumber
              min={BANNER_PRIORITY_MIN}
              max={BANNER_PRIORITY_MAX}
              style={{ width: "100%" }}
            />
          </Item>
          <Item name="liveBanner" label="Live Banner">
            <TextArea rows={2} disabled={!useCustomBanners} />
          </Item>
          <Item
            name="liveBannerPriority"
            label={
              <span>
                Live Banner Priority{" "}
                <Text type="secondary" style={{ fontSize: 11 }}>
                  (default: {PHASE_DEFAULT_PRIORITIES.live})
                </Text>
              </span>
            }
            tooltip={`Controls display order when multiple banners compete. Default auto-assigned = ${PHASE_DEFAULT_PRIORITIES.live}. Override only when you get a collision error.`}
            rules={[
              {
                type: "number",
                min: BANNER_PRIORITY_MIN,
                max: BANNER_PRIORITY_MAX,
                message: `Must be ${BANNER_PRIORITY_MIN}–${BANNER_PRIORITY_MAX}`
              }
            ]}
          >
            <InputNumber
              min={BANNER_PRIORITY_MIN}
              max={BANNER_PRIORITY_MAX}
              style={{ width: "100%" }}
            />
          </Item>
          <Item name="pendingBanner" label="Recording Pending Banner">
            <TextArea rows={2} disabled={!useCustomBanners} />
          </Item>
          <Item
            name="pendingBannerPriority"
            label={
              <span>
                Pending Banner Priority{" "}
                <Text type="secondary" style={{ fontSize: 11 }}>
                  (default: {PHASE_DEFAULT_PRIORITIES.pending})
                </Text>
              </span>
            }
            tooltip={`Controls display order when multiple banners compete. Default auto-assigned = ${PHASE_DEFAULT_PRIORITIES.pending}. Override only when you get a collision error.`}
            rules={[
              {
                type: "number",
                min: BANNER_PRIORITY_MIN,
                max: BANNER_PRIORITY_MAX,
                message: `Must be ${BANNER_PRIORITY_MIN}–${BANNER_PRIORITY_MAX}`
              }
            ]}
          >
            <InputNumber
              min={BANNER_PRIORITY_MIN}
              max={BANNER_PRIORITY_MAX}
              style={{ width: "100%" }}
            />
          </Item>
        </div>
      </Form>
    </Modal>
  );
};

export default CreateEditSessionModal;
